'use memo';

import { useEffect, useState } from 'react';
import {
  SellersDisclosurePreparedByModel,
  SellersDisclosurePreparedByModelPreparedBy,
  SellersDisclosureRequiredSearch,
} from '@urbanx/agx.campaigns.client';

import './SellersDisclosure.scss';
import {
  AgxRadioGroup,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
  formatCurrency,
} from '@urbanx/agx.ui-components2';

interface SellersDisclosureProps {
  id: string;
  label?: string;
  onValueChanged: (value: { id: string; value: SellersDisclosurePreparedByModel }) => void;
  defaultValue?: SellersDisclosurePreparedByModel;
  requiredSearches: SellersDisclosureRequiredSearch[];
  required?: boolean;
  validate?: boolean;
}

const SellersDisclosure = ({
  id,
  label,
  onValueChanged,
  defaultValue,
  requiredSearches,
  required,
  validate,
}: SellersDisclosureProps) => {
  const [preparedByDetails, setPreparedByDetails] = useState<
    SellersDisclosurePreparedByModel | undefined
  >(defaultValue);
  const selectedPreparedBy = preparedByDetails?.preparedBy;
  const isThirdPartySelected =
    selectedPreparedBy === SellersDisclosurePreparedByModelPreparedBy.ThirdParty;
  const isAgentSelected = selectedPreparedBy === SellersDisclosurePreparedByModelPreparedBy.Agent;

  useEffect(() => {
    if (!preparedByDetails) return;

    onValueChanged({ id, value: preparedByDetails });
  }, [id, onValueChanged, preparedByDetails]);

  const renderThirdPartySubContent = () => {
    if (!isThirdPartySelected) return undefined;

    return [
      <div key="third-party-fields" className="sellersDisclosure__thirdPartyFields">
        <AgxSpacer direction="vertical" gap="sm" fill>
          <AgxTextInput
            id="thirdPartyName"
            label="Name of third party"
            required
            stretch
            optionalText=""
            defaultValue={preparedByDetails?.thirdPartyDetails?.thirdPartyName}
            onInputValueChange={({ value }) => {
              setPreparedByDetails((prev) => ({
                ...prev,
                thirdPartyDetails: {
                  ...prev?.thirdPartyDetails,
                  thirdPartyName: value,
                },
              }));
            }}
            errorText={
              validate && !preparedByDetails?.thirdPartyDetails?.thirdPartyName
                ? 'Enter third party name'
                : undefined
            }
            type={'text'}
          />

          <AgxTextInput
            id="thirdPartyFee"
            label="Fee "
            required
            stretch
            optionalText=""
            defaultValue={preparedByDetails?.thirdPartyDetails?.thirdPartyFee}
            onInputValueChange={({ value }) => {
              setPreparedByDetails((prev) => ({
                ...prev,
                thirdPartyDetails: {
                  ...prev?.thirdPartyDetails,
                  thirdPartyFee: value,
                },
              }));
            }}
            errorText={
              validate && !preparedByDetails?.thirdPartyDetails?.thirdPartyFee
                ? 'Enter third party fee'
                : undefined
            }
            type={'number'}
          />
        </AgxSpacer>
      </div>,
    ];
  };

  const renderRequiredSearch = (search: SellersDisclosureRequiredSearch, index: number) => (
    <li key={index}>
      <AgxTypography type="body_ui" size="small" id={`agx-agentDisclosureTitle-${index + 1}`}>
        {search.searchName}{' '}
      </AgxTypography>
      <AgxTypography id={`agx-agentDisclosureCost-${index + 1}`} type="body_ui" size="small">
        {formatCurrency(search.cost ?? 0, 2)}{' '}
      </AgxTypography>
      <AgxTypography type="body_ui" size="small" id={`agx-agentDisclosureApplicable-${index + 1}`}>
        {search.isOptional ? '(if applicable)' : ''}
      </AgxTypography>
    </li>
  );

  const renderAgentSubContent = () => {
    if (!isAgentSelected) return undefined;

    return [
      <div className="sellersDisclosure">
        <AgxSpacer direction="vertical" id="required-searches" gap="md">
          <AgxTypography size="small" type="body_ui" id="agx-agentDisclosure-description">
            Required The below searches are required. Please ensure you include them in your
            marketing package.
          </AgxTypography>
          <div className="sellersDisclosure__requiredSearches ">
            <ul>{requiredSearches.map(renderRequiredSearch)}</ul>
          </div>
        </AgxSpacer>
      </div>,
    ];
  };
  const onPreparedByChange = (value: string) => {
    switch (value) {
      case SellersDisclosurePreparedByModelPreparedBy.VendorSolicitor:
        setPreparedByDetails({
          preparedBy: SellersDisclosurePreparedByModelPreparedBy.VendorSolicitor,
          thirdPartyDetails: undefined,
        });
        return;
      case SellersDisclosurePreparedByModelPreparedBy.ThirdParty:
        setPreparedByDetails((prev: any) => ({
          ...prev,
          preparedBy: SellersDisclosurePreparedByModelPreparedBy.ThirdParty,
        }));
        return;
      case SellersDisclosurePreparedByModelPreparedBy.Agent:
        setPreparedByDetails({
          preparedBy: SellersDisclosurePreparedByModelPreparedBy.Agent,
          thirdPartyDetails: undefined,
        });
        return;
      case SellersDisclosurePreparedByModelPreparedBy.Exempt:
        setPreparedByDetails({
          preparedBy: SellersDisclosurePreparedByModelPreparedBy.Exempt,
          thirdPartyDetails: undefined,
        });
        return;
      default:
        return;
    }
  };

  const radioSubContent = isThirdPartySelected
    ? renderThirdPartySubContent()
    : isAgentSelected
      ? renderAgentSubContent()
      : undefined;

  return (
    <div className="agxRadio sellersDisclosure" data-testid={id}>
      <AgxTypography id="whoispreparing-title" size="medium" type="label">
        {label}
      </AgxTypography>
      <AgxRadioGroup
        id={id}
        layout="stacked"
        value={selectedPreparedBy}
        onChange={onPreparedByChange}
        options={[
          {
            label: "Vendor's Solicitor",
            value: SellersDisclosurePreparedByModelPreparedBy.VendorSolicitor,
          },
          {
            label: 'Third Party',
            value: SellersDisclosurePreparedByModelPreparedBy.ThirdParty,
          },
          {
            label: 'Agent',
            value: SellersDisclosurePreparedByModelPreparedBy.Agent,
          },
          {
            label: 'Exempt',
            value: SellersDisclosurePreparedByModelPreparedBy.Exempt,
          },
        ]}
        subContent={radioSubContent}
        subContentVariant={radioSubContent ? 'panel' : 'default'}
        required={required}
        validate={validate}
        errorText={!selectedPreparedBy ? 'Select option' : ''}
      />
    </div>
  );
};

export default SellersDisclosure;
